import type { Customer, FeatureToggles } from '../types/customer';
import {
  MEU_TIM,
  CAPTIVE_PORTAL,
  TIM_ATIVACAO_CHIP,
} from '../constants/channels';
import type { RecurrenceOutcome, RecurrenceResult } from '../types/recharge';

interface RecurrenceItem {
  title: string;
  subtitle: string;
}

/**
 * Textos da linha de recarga programada na tela de sucesso da recarga, por
 * desfecho da consulta de adesão.
 *
 * Título e subtítulo ficam juntos de propósito: os dois variam conforme o
 * desfecho, e separá-los abriria espaço para alterar um sem o outro.
 *
 * O texto de sucesso veio do time de UX e é fixo: não usa a `renewalDate` que o
 * BFF devolve, e a periodicidade está escrita à mão porque a API não informa a
 * frequência — só `status` e `renewalDate`.
 */
const RECURRENCE_ITEM_BY_OUTCOME: Record<RecurrenceOutcome, RecurrenceItem> = {
  success: {
    title: 'Frequência de recarga programada',
    subtitle: 'A cada 30 dias - Edite ou cancele quando quiser.',
  },
  processing: {
    title: 'Recarga programada em processamento',
    subtitle: 'Avisaremos por SMS quando for concluída.',
  },
  error: {
    title: 'Recarga programada',
    subtitle: 'Não foi possível ativar a recarga programada.',
  },
};

/**
 * Devolve null quando não houve aceite dentro do checkout — aí a consulta nem é
 * disparada e a linha não aparece na tela. Tendo aceite, todo desfecho é
 * exibido, inclusive a falha.
 *
 * `fromCheckout` é obrigatório e não é redundante: o resultado da programada
 * sobrevive na store até o próximo `createCheckoutSession`, e os fluxos FORA do
 * checkout (recarga avulsa e repetir recarga no crédito, via
 * `confirmacao-recarga-avulsa`) chegam na mesma tela de sucesso sem passar por
 * ele. Sem este gate, uma recarga avulsa feita logo depois de um checkout com
 * adesão exibiria a programada da recarga anterior.
 */
export const buildRecurrenceItem = (
  result: RecurrenceResult | null | undefined,
  fromCheckout: boolean | undefined,
): RecurrenceItem | null => {
  if (!fromCheckout || !result) return null;

  return RECURRENCE_ITEM_BY_OUTCOME[result.outcome] ?? null;
};

/**
 * Diz se o `recurrence_status` do evento de clique `payment_request` do
 * checkout é um aceite da programada. `on` é o checkbox marcado e `auto` é o
 * aceite automático; `off` e a ausência do campo (oferta não exibida) são
 * recusa.
 *
 * Mora aqui porque duas camadas decidem pela mesma regra: o `useCheckout`, para
 * consultar a adesão, e o tracking do clique em Pagar, para o label do GA4.
 */
export const isRecurrenceOptinAccepted = (
  status: string | null | undefined,
): boolean => !!status && status !== 'off';

/**
 * Diz se o cliente tem recarga programada ativa, pela trait
 * `scheduled_topup_status` do payload do customer.
 *
 * A regra espelha a do BFF (`CreateSmartCheckoutSession#_resolveRecurrenceOffer`):
 * qualquer valor diferente de `canceled` conta como ativa. Espelhar em vez de
 * enumerar os status possíveis é deliberado — o BFF só testa contra `canceled`,
 * e uma lista nossa ficaria desatualizada em silêncio no dia em que aparecesse
 * um status novo.
 *
 * Gateia três coisas, e por isso mora aqui em vez de repetida em cada hook: o
 * BFF não ofertar a programada dentro do checkout, o card de gerenciar na
 * valores-recarga, e esconder o repetir-recarga.
 */
export const hasScheduledTopup = (
  customer: Customer | null | undefined,
): boolean => {
  const status = customer?.traits?.scheduled_topup_status?.value;

  return !!status && status !== 'canceled';
};

const RECURRENCE_CHANNELS: readonly string[] = [
  MEU_TIM,
  CAPTIVE_PORTAL,
  TIM_ATIVACAO_CHIP,
];

/**
 * Diz se o cliente pode gerenciar a programada pelo fluxo novo — e, por tabela,
 * se o repetir-recarga deve sumir da tela.
 *
 * As duas decisões chamam ESTA função de propósito. Esconder o repetir-recarga
 * em canal onde o card de gerenciar não aparece deixaria o cliente sem os dois:
 * perderia o widget sem nada no lugar. Manter a simetria por construção, e não
 * por coincidência de condições copiadas, é o ponto.
 */
export const canManageScheduledTopup = ({
  customer,
  featureToggles,
  channel,
}: {
  customer: Customer | null | undefined;
  featureToggles: FeatureToggles | null | undefined;
  channel: string | null | undefined;
}): boolean =>
  !!featureToggles?.smartCheckout?.recurrenceCheckout &&
  !!channel &&
  RECURRENCE_CHANNELS.includes(channel) &&
  hasScheduledTopup(customer);
